import { useState } from 'react';
import { api, errorMessage } from '../api/client';
import { Avatar, bumpPhoto } from './Avatar';
import { Alert, Button } from './components';

/** Reduce la foto a 512 px (JPG) antes de subirla: pesa poco y carga rápido. Si no se puede, va tal cual. */
async function shrink(file: File): Promise<File> {
  try {
    if (typeof createImageBitmap !== 'function') return file;
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 512 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    return blob ? new File([blob], 'foto.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}

/** Elegir, cambiar o quitar la foto de perfil (propia o, para la administración, de un miembro). */
export function PhotoPicker({ personId, name, self, buttonsOnly = false }: { personId: string; name: string; self: boolean; buttonsOnly?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const path = self ? '/identity/me/photo' : `/identity/people/${personId}/photo`;
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.putFile(path, await shrink(file));
      bumpPhoto(personId);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    try {
      await api.delete(path);
      bumpPhoto(personId);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="avatar-row">
      {buttonsOnly ? null : <Avatar personId={personId} name={name} size={72} />}
      <div className="actions actions--wrap">
        <label className={`btn btn--secondary${busy ? ' btn--busy' : ''}`}>
          {self ? 'Cambiar mi foto' : 'Cambiar foto'}
          <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={busy} onChange={(e) => void pick(e)} aria-label={self ? 'Elegir mi foto' : 'Elegir foto'} />
        </label>
        <Button variant="ghost" busy={busy} onClick={() => void remove()}>Quitar foto</Button>
      </div>
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}
